import {
  ALLERGY_CATEGORIES,
  DIET_ORDER_STATUSES,
  FLUID_RESTRICTION_LIMITS,
  KITCHEN_NOTE_MAX_LENGTH,
  MEALS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { capturedListValueSchema, operationsListItemSchema } from './operations.ts';

// Diet orders and the kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1.3). The
// kitchen note travels in bodies only, never in URLs. The kitchen's meal list carries what the
// kitchen needs and nothing more: no diagnosis, admission reason or reaction text.

export const dietOptionsSchema = z.object({
  diets: z.array(operationsListItemSchema),
  textures: z.array(operationsListItemSchema),
  meals: z.array(z.object({ meal: z.enum(MEALS), label: z.string(), at: z.string() })),
});
export type DietOptionsView = z.infer<typeof dietOptionsSchema>;

export const placeDietOrderRequestSchema = z.object({
  /** Generated once per order; a retried request returns the same order. */
  clientRequestId: z.uuid(),
  dietCode: z.string().trim().min(1).max(40),
  textureCode: z.string().trim().min(1).max(40).nullable(),
  fluidRestrictionMl: z
    .number()
    .int()
    .min(FLUID_RESTRICTION_LIMITS.min)
    .max(FLUID_RESTRICTION_LIMITS.max)
    .nullable(),
  nilByMouth: z.boolean(),
  nbmFrom: z.iso.datetime().nullable(),
  nbmUntil: z.iso.datetime().nullable(),
  kitchenNote: z.string().max(KITCHEN_NOTE_MAX_LENGTH).nullable(),
});
export type PlaceDietOrderRequest = z.infer<typeof placeDietOrderRequestSchema>;

export const dietOrderEnteredInErrorRequestSchema = z.object({ reason: reasonSchema });

export const dietOrderSchema = z.object({
  id: z.uuid(),
  admissionId: z.uuid(),
  diet: capturedListValueSchema,
  texture: capturedListValueSchema.nullable(),
  fluidRestrictionMl: z.number().int().nullable(),
  nilByMouth: z.boolean(),
  nbmFrom: z.iso.datetime().nullable(),
  nbmUntil: z.iso.datetime().nullable(),
  kitchenNote: z.string().nullable(),
  /** "Diabetic, soft texture, fluids 1,500 mL a day". */
  words: z.string(),
  orderedAt: z.iso.datetime(),
  orderedBy: actorSchema,
  status: z.enum(DIET_ORDER_STATUSES),
  statusReason: z.string().nullable(),
  statusChangedAt: z.iso.datetime().nullable(),
  statusChangedBy: actorSchema.nullable(),
});
export type DietOrderView = z.infer<typeof dietOrderSchema>;

/** A stay's diet: the current order (or none) and every version, newest first. */
export const stayDietSchema = z.object({
  admissionId: z.uuid(),
  admitted: z.boolean(),
  current: dietOrderSchema.nullable(),
  history: z.array(dietOrderSchema),
});
export type StayDietView = z.infer<typeof stayDietSchema>;

/** The patients in a ward with their current diet, for ordering from the ward. */
export const wardDietsSchema = z.object({
  ward: z.object({ id: z.uuid(), name: z.string() }),
  patients: z.array(
    z.object({
      admissionId: z.uuid(),
      bedLabel: z.string().nullable(),
      /** Null when the reader may not see the stay (sensitivity rule). */
      patient: z
        .object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() })
        .nullable(),
      current: dietOrderSchema.nullable(),
    }),
  ),
});
export type WardDietsView = z.infer<typeof wardDietsSchema>;

export const kitchenWardsSchema = z.object({
  wards: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type KitchenWardsView = z.infer<typeof kitchenWardsSchema>;

/**
 * Allergies as the kitchen sees them: always explicit (CLAUDE.md), substances and their
 * category only (plan K4).
 */
export const kitchenAllergiesSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('list'),
    allergies: z
      .array(z.object({ substance: z.string(), category: z.enum(ALLERGY_CATEGORIES).nullable() }))
      .min(1),
  }),
  z.object({ state: z.literal('no_known_allergies') }),
  z.object({ state: z.literal('not_recorded') }),
]);
export type KitchenAllergiesView = z.infer<typeof kitchenAllergiesSchema>;

export const mealListQuerySchema = z.object({
  /** Absent: every ward at the facility. */
  wardId: z.uuid().optional(),
  date: isoDaySchema,
  meal: z.enum(MEALS),
});
export type MealListQuery = z.infer<typeof mealListQuerySchema>;

/** One patient on the meal list. Strict: the kitchen gets these fields and no others. */
export const mealListRowSchema = z
  .object({
    admissionId: z.uuid(),
    wardName: z.string(),
    /** Null while waiting for a bed in the ward. */
    bedLabel: z.string().nullable(),
    /** Null for a restricted record (plan K7): bed, diet and allergies still shown. */
    patient: z.object({ displayName: z.string(), patientNumber: z.string() }).strict().nullable(),
    /** Null: no diet ordered, ask the ward. */
    diet: z
      .object({
        diet: z.string(),
        texture: z.string().nullable(),
        fluidRestrictionMl: z.number().int().nullable(),
        /** Hidden (null) for a restricted record. */
        kitchenNote: z.string().nullable(),
      })
      .strict()
      .nullable(),
    allergies: kitchenAllergiesSchema,
    /** Set when nil by mouth at this meal: the end, or null until the order is changed. */
    nilByMouth: z.object({ until: z.iso.datetime().nullable() }).strict().nullable(),
  })
  .strict();
export type MealListRow = z.infer<typeof mealListRowSchema>;

export const mealListSchema = z
  .object({
    date: isoDaySchema,
    meal: z.enum(MEALS),
    mealLabel: z.string(),
    servedAt: z.iso.datetime(),
    ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    /** To be served, by ward and bed. */
    serve: z.array(mealListRowSchema),
    /** Nil by mouth at this meal: no meal. */
    nilByMouth: z.array(mealListRowSchema),
    /** Meals to prepare by diet, for those served. */
    counts: z.array(z.object({ diet: z.string(), count: z.number().int() })),
  })
  .strict();
export type MealListView = z.infer<typeof mealListSchema>;

export const mealTicketsPrintedRequestSchema = z.object({
  wardId: z.uuid().nullable(),
  date: isoDaySchema,
  meal: z.enum(MEALS),
  tickets: z.number().int().min(0).max(1000),
});
export type MealTicketsPrintedRequest = z.infer<typeof mealTicketsPrintedRequestSchema>;
